import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Search, Download } from 'lucide-react';
import { fetchServiceDurationReport } from '../../services/reportsApi';
import * as XLSX from 'xlsx';
import useEmployeeMasters from '../../hooks/useEmployeeMasters';
import { formatDate } from '../../utils/dateUtils';

const ServiceDurationReportView = () => {
  const { hasPermission } = useAuth();
  const { masters } = useEmployeeMasters();

  const [designationId, setDesignationId] = useState('all');
  const [years, setYears] = useState('10');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const abortControllerRef = useRef(null);

  const loadData = async () => {
    if (!designationId || !years) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError('');

    try {
      const filters = {
        designation_id: designationId,
        years: years,
      };

      const response = await fetchServiceDurationReport(filters, abortControllerRef.current.signal);
      setData(response.items || []);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch service duration report');
      }
    } finally {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [designationId, years]);

  const filteredData = data.filter(emp => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      (emp.emp_code && emp.emp_code.toLowerCase().includes(lowerQuery)) ||
      (emp.full_name && emp.full_name.toLowerCase().includes(lowerQuery)) ||
      (emp.department && emp.department.toLowerCase().includes(lowerQuery)) ||
      (emp.designation && emp.designation.toLowerCase().includes(lowerQuery))
    );
  });

  const downloadExcel = () => {
    if (filteredData.length === 0) return;

    const exportData = filteredData.map(item => ({
      'Employee Code': item.emp_code,
      'Employee Name': item.full_name,
      'Department': item.department || '—',
      'Designation': item.designation || '—',
      'Company Joining Date': formatDate(item.company_joining_date),
      'Cumulative Tenure in Selected Designation': item.service_duration,
      'Promotional Promotion Date': formatDate(item.promotional_promotion_date),
      'Functional Promotion Date — 10 Years': formatDate(item.functional_promotion_10_years),
      'Functional Promotion Date — 20 Years': formatDate(item.functional_promotion_20_years),
      'Functional Promotion Date — 30 Years': formatDate(item.functional_promotion_30_years),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Service Duration');
    XLSX.writeFile(workbook, `Service_Duration_Report.xlsx`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters Area */}
      <div className="bg-white p-4 border-b border-gray-200">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Select Designation
              </label>
              <select
                value={designationId}
                onChange={(e) => setDesignationId(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[200px]"
              >
                <option value="all">All</option>
                {(masters?.designations || []).map((desig) => (
                  <option key={desig.value} value={desig.value.toString()}>
                    {desig.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Service Years
              </label>
              <select
                value={years}
                onChange={(e) => setYears(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[150px]"
              >
                <option value="10">10 Years</option>
                <option value="20">20 Years</option>
                <option value="30">30 Years</option>
              </select>
            </div>

            <div className="self-end pb-0.5">
              <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
                Total: <strong className="ml-1 text-gray-900">{filteredData.length}</strong>&nbsp;{filteredData.length === 1 ? 'employee' : 'employees'}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm w-48 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            {hasPermission('report:export') && (
              <button
                onClick={downloadExcel}
                disabled={filteredData.length === 0}
                className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border-separate border-spacing-0">
              <thead className="bg-gray-50">
                <tr>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Employee Code</th>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Employee Name</th>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Department</th>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Designation</th>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Company Joining Date</th>
                  <th rowSpan="2" className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Cumulative Tenure in Selected Designation</th>
                  <th rowSpan="2" className="px-5 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">Promotional Promotion Date</th>
                  <th colSpan="3" className="px-5 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 bg-gray-100 whitespace-nowrap">Functional Promotion Date</th>
                </tr>
                <tr>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">10 Years</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap">20 Years</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 whitespace-nowrap">30 Years</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!loading && filteredData.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="h-12 w-12 text-gray-300 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                          <Search className="w-6 h-6" />
                        </div>
                        <p className="text-gray-500 text-sm">No records found.</p>
                      </div>
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Loading data...</p>
                    </td>
                  </tr>
                )}
                {!loading && filteredData.length > 0 && filteredData.map((emp, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-100">{emp.emp_code}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-100">{emp.full_name}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-100">{emp.department || '—'}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-100">{emp.designation || '—'}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600 border-r border-gray-100">
                      {formatDate(emp.company_joining_date)}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm font-medium text-blue-600 border-r border-gray-100">
                      {emp.service_duration}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm text-center text-gray-700 font-medium border-r border-gray-100">
                      {formatDate(emp.promotional_promotion_date)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-center text-gray-700 border-r border-gray-100">
                      {formatDate(emp.functional_promotion_10_years)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-center text-gray-700 border-r border-gray-100">
                      {formatDate(emp.functional_promotion_20_years)}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm text-center text-gray-700">
                      {formatDate(emp.functional_promotion_30_years)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDurationReportView;
